import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import type { SeminarClaimableCoupon } from '@/api/repository/types/types';
import CustomModal from '@/components/common/elements/Modal';
import { CouponList, CouponNotes, CouponRow, CouponTag, CouponAction } from '@/components/domain/coupon/CouponRow';
import { calcDiscount, COUPON_NO_RESTORE_NOTICE, COUPON_RESTORE_NOTICE, couponAmountSub, couponAmountText, isPlatformIssuer, scopeText, untilText } from '@/components/domain/coupon/couponFormat';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import { DCard, DCardTitle, PRIMARY } from './detailStyles';
import type { ClaimedCouponState } from './useSeminarCoupons';
import { ChevronLabel } from '@/components/common/elements/IconLabel';

/*
 * 세미나 상세 "쿠폰 N장" 카드 — 정보 표 아래(시안 r1-detail-claim). 받기 전용.
 * 카드에는 이 세미나 정가 기준 할인이 가장 큰 1장만 보여 주고, 여러 장이면 오른쪽 위 "더보기"로 팝업을 열어 전부 보여 준다.
 * (결제 모달은 고르는 화면이라 접기 방식 — PurchaseCouponPicker)
 */

const TitleLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const CountPill = styled.span`
  padding: 1px 8px;
  border-radius: 999px;
  background: #e8f2f1;
  color: ${PRIMARY};
  font-size: 12px;
  font-weight: 800;
  line-height: 1.7;
`;

const MoreLink = styled.button`
  padding: 4px 0 4px 8px;
  border: 0;
  background: none;
  color: #6b7280;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${PRIMARY};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const PopupBody = styled.div`
  padding: 4px 0 8px;
`;

const restoreLine = (coupons: SeminarClaimableCoupon[]): string => {
  const restoring: number = coupons.filter((c: SeminarClaimableCoupon) => c.restoreOnRefund).length;
  if (restoring === 0) return COUPON_NO_RESTORE_NOTICE;
  if (restoring === coupons.length) return COUPON_RESTORE_NOTICE;
  return '환불 시 쿠폰 복원 여부는 쿠폰마다 달라요. 폐강되거나 강사가 신청을 거절하면 돌려드려요.';
};

const isSoldOut = (coupon: SeminarClaimableCoupon): boolean =>
  !coupon.claimed && (coupon.soldOut === true || coupon.remaining === 0);

/** 이 세미나 정가 기준 할인액 — 서버 previewDiscount 우선, 없으면 정가로 계산 */
const discountOf = (coupon: SeminarClaimableCoupon, listPrice: number): number =>
  coupon.previewDiscount ?? calcDiscount(coupon.discountType, coupon.discountValue, coupon.maxDiscountAmount, listPrice);

/** 할인 큰 순(마감은 뒤로) — 첫 장이 "가장 좋은 쿠폰" */
const sortByBestDiscount = (coupons: SeminarClaimableCoupon[], listPrice: number): SeminarClaimableCoupon[] =>
  [...coupons].sort((a: SeminarClaimableCoupon, b: SeminarClaimableCoupon) => {
    const soldOutDiff: number = Number(isSoldOut(a)) - Number(isSoldOut(b));
    if (soldOutDiff !== 0) return soldOutDiff;
    return discountOf(b, listPrice) - discountOf(a, listPrice);
  });

const CLAIMED_LABEL: Record<ClaimedCouponState, string> = { ready: '받음', reserved: '결제 중', used: '사용함' };

interface SeminarCouponCardProps {
  coupons: SeminarClaimableCoupon[];
  /** 세미나 정가 — 가장 좋은 쿠폰을 고르는 기준 */
  listPrice: number;
  claimingId: number | null;
  onClaim: (coupon: SeminarClaimableCoupon) => void;
  /** 받은 쿠폰의 지금 상태(받음 / 결제 중 / 사용함) */
  claimedStateOf: (coupon: SeminarClaimableCoupon) => ClaimedCouponState;
}

const SeminarCouponCard: React.FC<SeminarCouponCardProps> = ({ coupons, listPrice, claimingId, onClaim, claimedStateOf }) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const sorted: SeminarClaimableCoupon[] = useMemo(() => sortByBestDiscount(coupons, listPrice), [coupons, listPrice]);
  if (coupons.length === 0) return null;

  const renderRow = (coupon: SeminarClaimableCoupon): React.ReactNode => {
    const platform: boolean = isPlatformIssuer(coupon.issuerName, coupon.issuerType);
    const soldOut: boolean = isSoldOut(coupon);
    const tags: CouponTag[] = [];
    const until: string = untilText(coupon.endsAt);
    if (until) tags.push({ text: until });
    if (!coupon.claimed && typeof coupon.remaining === 'number' && coupon.remaining > 0) {
      tags.push({ text: `${coupon.remaining}장 남음`, tone: 'danger' });
    }
    const action: CouponAction = coupon.claimed
      ? { label: CLAIMED_LABEL[claimedStateOf(coupon)], variant: 'done' }
      : soldOut
        ? { label: '마감', variant: 'disabled' }
        : {
            label: claimingId === coupon.couponId ? <ButtonSpinner /> : '받기',
            variant: 'primary',
            busy: claimingId !== null,
            onClick: () => onClaim(coupon),
            ariaLabel: `${coupon.name} 쿠폰 받기`,
          };
    return (
      <CouponRow
        key={coupon.couponId}
        amount={couponAmountText(coupon)}
        amountSub={couponAmountSub(coupon)}
        title={coupon.name}
        description={platform
          ? `명치사 · ${scopeText(coupon.scopeLabel, coupon.scope)}`
          : `${coupon.issuerName}님이 드려요`}
        tags={tags}
        action={action}
        muted={soldOut}
      />
    );
  };

  const notes: string[] = ['받은 쿠폰은 결제할 때 자동으로 적용돼요.', '한 결제에 1장만 쓸 수 있어요.', restoreLine(coupons)];

  return (
    <DCard aria-labelledby="seminar-coupon-title">
      <DCardTitle id="seminar-coupon-title">
        <TitleLeft>
          쿠폰 <CountPill>{coupons.length}장</CountPill>
        </TitleLeft>
        {sorted.length > 1 && (
          <MoreLink type="button" onClick={() => setPopupOpen(true)} aria-haspopup="dialog">
            <ChevronLabel>더보기</ChevronLabel>
          </MoreLink>
        )}
      </DCardTitle>
      <CouponList>{renderRow(sorted[0])}</CouponList>
      <CouponNotes lines={notes} />

      {popupOpen && (
        <CustomModal
          title={`쿠폰 ${coupons.length}장`}
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          width="min(460px, calc(100vw - 2rem))"
          height="auto"
        >
          <PopupBody>
            <CouponList>{sorted.map(renderRow)}</CouponList>
            <CouponNotes lines={notes} />
          </PopupBody>
        </CustomModal>
      )}
    </DCard>
  );
};

export default SeminarCouponCard;
